import React, { useState } from 'react';
import { PackageOpen, CheckCircle, CreditCard, Activity, Clock, FileText, Play } from 'lucide-react';
import api from '../api';

const STATUS_CONFIG = {
  created: { label: 'Criado', color: 'var(--text-secondary)', icon: PackageOpen },
  pending: { label: 'Pendente', color: 'var(--warning)', icon: Clock },
  paid: { label: 'Pago', color: '#61ffb2', icon: CreditCard },
  approved: { label: 'Aprovado', color: 'var(--accent)', icon: CheckCircle },
  simulated: { label: 'Simulado', color: '#d8b4fe', icon: Play },
  requested_export: { label: 'Export. Solicitada', color: '#60a5fa', icon: FileText },
};

export default function AdminOrdersTable({ orders, onStatusChange }) {
  const [loadingOrderId, setLoadingOrderId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Sorting orders by date descending (newest first)
  const sortedOrders = [...orders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const handleAction = async (e, orderId, actionType) => {
    e.stopPropagation(); // Evita expandir a linha ao clicar no botão
    if (!window.confirm(`Tem certeza que deseja marcar este pedido como ${actionType}?`)) return;
    
    setLoadingOrderId(orderId);
    try {
      if (actionType === 'APROVADO') {
        await api.post(`/orders/${orderId}/approve`);
      } else if (actionType === 'PAGO') {
        await api.post(`/orders/${orderId}/mark_paid`);
      } else if (actionType === 'SIMULADO') {
        await api.post(`/orders/${orderId}/simulate`);
      }
      if (onStatusChange) onStatusChange();
    } catch (err) {
      alert(`Erro ao atualizar pedido: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingOrderId(null);
    }
  };

  if (orders.length === 0) {
    return (
      <div className="admin-detail-card" style={{ marginBottom: '2rem' }}>
        <h3 className="admin-detail-card__title">Pedidos Recentes</h3>
        <p style={{ color: 'var(--text-secondary)' }}>Nenhum pedido encontrado no sistema.</p>
      </div>
    );
  }

  return (
    <div className="admin-detail-card" style={{ marginBottom: '2rem', overflowX: 'auto', background: 'rgba(20,20,20,0.4)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3 className="admin-detail-card__title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: '500' }}>
          <PackageOpen size={18} style={{ verticalAlign: 'middle', marginRight: '8px', color: 'var(--accent)' }} />
          Pedidos Recentes
        </h3>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Clique na linha para ver detalhes</span>
      </div>
      
      <table className="leads-table" style={{ minWidth: '700px', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'transparent' }}>
            <th style={{ color: 'var(--text-secondary)', fontWeight: '500', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>Data</th>
            <th style={{ color: 'var(--text-secondary)', fontWeight: '500', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>Solicitação</th>
            <th style={{ color: 'var(--text-secondary)', fontWeight: '500', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>Status</th>
            <th style={{ textAlign: 'center', color: 'var(--text-secondary)', fontWeight: '500', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {sortedOrders.map((o) => {
            const st = STATUS_CONFIG[o.status] || { label: o.status, color: 'var(--text-secondary)', icon: Activity };
            const StatusIcon = st.icon;
            
            const notesMatch = (o.notes || '').match(/items=(\d+)/);
            const leadsCount = notesMatch ? parseInt(notesMatch[1]) : (o.packs || 1) * 25;
            
            const dateObj = new Date(o.created_at);
            const dateStr = dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
            const timeStr = dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            
            const isProcessing = loadingOrderId === o.id;
            const isExpanded = expandedId === o.id;

            return (
              <React.Fragment key={o.id}>
                <tr 
                  onClick={() => setExpandedId(isExpanded ? null : o.id)}
                  style={{ 
                    cursor: 'pointer', 
                    background: isExpanded ? 'rgba(255,255,255,0.03)' : 'transparent',
                    transition: 'background 0.2s',
                    borderBottom: isExpanded ? 'none' : '1px solid rgba(255,255,255,0.03)'
                  }}
                >
                  <td style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                    {dateStr} <span style={{ color: 'var(--text-secondary)' }}>{timeStr}</span>
                  </td>
                  <td style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    {leadsCount} Leads
                  </td>
                  <td>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      backgroundColor: `${st.color}15`,
                      color: st.color,
                      fontSize: '0.7rem',
                      fontWeight: '500',
                      border: `1px solid ${st.color}30`
                    }}>
                      <StatusIcon size={12} />
                      {st.label}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                      {o.status !== 'approved' && (
                        <button
                          className="glass"
                          style={{ padding: '4px 10px', fontSize: '0.7rem', color: 'var(--accent)', border: '1px solid rgba(var(--accent-rgb), 0.2)' }}
                          disabled={isProcessing}
                          onClick={(e) => handleAction(e, o.id, 'APROVADO')}
                        >
                          {isProcessing ? '...' : 'Aprovar'}
                        </button>
                      )}
                      {o.status !== 'paid' && o.status !== 'approved' && (
                        <button
                          className="glass"
                          style={{ padding: '4px 10px', fontSize: '0.7rem', color: '#61ffb2', border: '1px solid rgba(97, 255, 178, 0.2)' }}
                          disabled={isProcessing}
                          onClick={(e) => handleAction(e, o.id, 'PAGO')}
                        >
                          Pago
                        </button>
                      )}
                      <button
                          className="glass"
                          style={{ padding: '4px 10px', fontSize: '0.7rem' }}
                          disabled={isProcessing}
                          onClick={(e) => handleAction(e, o.id, 'SIMULADO')}
                        >
                          Simular
                      </button>
                    </div>
                  </td>
                </tr>
                {isExpanded && (
                  <tr style={{ background: 'rgba(255,255,255,0.01)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td colSpan="4" style={{ padding: '0.5rem 1rem 1rem 1rem' }}>
                      <div style={{ display: 'flex', gap: '2rem', fontSize: '0.8rem', color: 'var(--text-secondary)', backgroundColor: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '8px' }}>
                        <div>
                          <strong style={{ display: 'block', marginBottom: '4px', color: 'var(--text-primary)' }}>E-mail (Cliente)</strong>
                          {o.owner || 'Não informado'}
                        </div>
                        <div>
                          <strong style={{ display: 'block', marginBottom: '4px', color: 'var(--text-primary)' }}>ID do Pedido</strong>
                          <span style={{ fontFamily: 'monospace' }}>{o.id}</span>
                        </div>
                        <div>
                          <strong style={{ display: 'block', marginBottom: '4px', color: 'var(--text-primary)' }}>Detalhes</strong>
                          Tier: {o.tier || 'standard'} | {o.packs || 1} Pacote(s)
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
